import {
  BRAND_COPY,
  REFERRAL_CAP_REACHED_MESSAGE,
  REFERRAL_DISABLED_MESSAGE,
  REFERRAL_INVALID_MESSAGE,
} from '@fyf/shared';
import { useCallback, useState } from 'react';

import { createSubmission, SubmissionPausedError } from '../api/client.js';
import { FormField } from '../components/FormField.js';
import { ScreenLayout } from '../components/ScreenLayout.js';
import { StepIndicator } from '../components/StepIndicator.js';
import { SubmitButton } from '../components/SubmitButton.js';
import { useCaptureDispatch, useCaptureState } from '../context/CaptureFlowContext.js';
import { validateContact } from '../utils/validate.js';

const { contact, paused } = BRAND_COPY.screens;

export function ContactScreen() {
  const state = useCaptureState();
  const dispatch = useCaptureDispatch();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [pausedMessage, setPausedMessage] = useState<string | null>(null);

  const updateField = useCallback(
    (field: string, value: string) => {
      dispatch({
        type: 'SET_CONTACT',
        payload: {
          firstName: state.firstName,
          lastName: state.lastName,
          displayName: state.displayName,
          email: state.email,
          organization: state.organization,
          [field]: value,
        },
      });
      // Clear error on type
      if (Object.hasOwn(errors, field)) {
        const { [field]: _, ...rest } = errors;
        setErrors(rest);
      }
    },
    [dispatch, state, errors]
  );

  const handleBlur = useCallback(
    (field: string) => {
      const all = validateContact({
        firstName: state.firstName,
        lastName: state.lastName,
        displayName: state.displayName,
        email: state.email,
        organization: state.organization,
      });
      const fieldError = Object.entries(all).find(([k]) => k === field)?.[1];
      if (fieldError) {
        setErrors((prev) => ({ ...prev, [field]: fieldError }));
      }
    },
    [state]
  );

  async function handleSubmit() {
    const validationErrors = validateContact({
      firstName: state.firstName,
      lastName: state.lastName,
      displayName: state.displayName,
      email: state.email,
      organization: state.organization,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setSubmitError(null);

    try {
      const result = await createSubmission({
        surfaceTag: state.surfaceTag!,
        firstName: state.firstName,
        lastName: state.lastName,
        displayName: state.displayName || undefined,
        email: state.email,
        organization: state.organization,
        freeText: state.freeText,
        superpower: state.superpower!,
        feeling: state.feeling!,
        ...(state.referralCode && { referredBy: state.referralCode }),
      });

      dispatch({ type: 'SET_SUBMISSION_ID', payload: result.id });
      dispatch({ type: 'SET_SCREEN', payload: 'generating' });
    } catch (err) {
      if (err instanceof SubmissionPausedError) {
        setIsPaused(true);
        setPausedMessage(err.message);
        setSubmitError(null);
      } else {
        setSubmitError(err instanceof Error ? err.message : 'Something went wrong.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleRetry() {
    setIsPaused(false);
    await handleSubmit();
  }

  if (isPaused) {
    const referralMessages: string[] = [
      REFERRAL_DISABLED_MESSAGE,
      REFERRAL_CAP_REACHED_MESSAGE,
      REFERRAL_INVALID_MESSAGE,
    ];
    const isReferralPause = !!pausedMessage && referralMessages.includes(pausedMessage);
    return (
      <ScreenLayout>
        <StepIndicator label={contact.stepLabel} currentStep={4} />
        <div className="text-center max-w-md mx-auto">
          <h2 className="text-xl font-semibold text-white mb-4">
            {isReferralPause && pausedMessage ? pausedMessage : paused.headline}
          </h2>
          {!isReferralPause && <p className="text-gray-300 mb-8">{paused.subtext}</p>}
          {isReferralPause ? (
            <a
              href="https://aduro.com"
              className="mt-6 inline-block px-6 py-2 text-sm font-medium rounded-full border border-orange-700/50 bg-orange-900/30 hover:bg-orange-800/50 text-orange-300 hover:text-white transition-colors"
            >
              Learn more about Aduro
            </a>
          ) : (
            <SubmitButton label={paused.button} onClick={handleRetry} loading={loading} />
          )}
        </div>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout>
      <StepIndicator currentStep={4} />
      <p className="font-['DM_Sans',sans-serif] text-base text-white text-center mb-10">
        Tell us who you are, and we&rsquo;ll bring your
        <br />
        flame to life.
      </p>

      <FormField
        label="First Name"
        name="firstName"
        value={state.firstName}
        onChange={(v) => updateField('firstName', v)}
        onBlur={() => handleBlur('firstName')}
        error={errors['firstName']}
        required
      />
      <FormField
        label="Last Name"
        name="lastName"
        value={state.lastName}
        onChange={(v) => updateField('lastName', v)}
        onBlur={() => handleBlur('lastName')}
        error={errors['lastName']}
        required
      />
      <FormField
        label="Display Name"
        name="displayName"
        value={state.displayName}
        onChange={(v) => updateField('displayName', v)}
        onBlur={() => handleBlur('displayName')}
        error={errors['displayName']}
        required
      />
      <FormField
        label="Email"
        name="email"
        type="email"
        value={state.email}
        onChange={(v) => updateField('email', v)}
        onBlur={() => handleBlur('email')}
        error={errors['email']}
        required
      />
      <FormField
        label="Organization"
        name="organization"
        value={state.organization}
        onChange={(v) => updateField('organization', v)}
        onBlur={() => handleBlur('organization')}
        error={errors['organization']}
        required
      />

      <p className="font-['DM_Sans',sans-serif] text-[12px] text-[#f9f7f4] opacity-60 leading-normal w-full">
        {contact.consent}
      </p>

      {submitError && (
        <p className="text-red-400 text-sm text-center mb-4" role="alert">
          {submitError}
        </p>
      )}

      <div className="mt-2 w-full max-w-md">
        <SubmitButton label={contact.button} onClick={handleSubmit} loading={loading} />
      </div>
    </ScreenLayout>
  );
}
